import type { CSSProperties, ReactNode } from 'react';
import { useId } from 'react';
import { css, styled } from 'styled-components';

import { useHideDocumentScroll } from '#/hooks/useHideDocumentScroll';
import { useIsMounted } from '#/hooks/useIsMounted';
import { CloseButton, Dialog, ModalBase, ModalTitle } from '#/modals/ModalBase';
import { colors, cssBreakpoints } from '#/theme/theme';

import type { ModalDismissalReason } from './ModalDismissalReason';

export const modalTopDecorationHeight = '7px';

export const getModalBackgroundImage = () => css`
  background-image: linear-gradient(
    to left,
    ${colors.hoverBlue},
    ${colors.secondarySkyBlue}
  );
  background-size: 100% ${modalTopDecorationHeight};
  background-repeat: no-repeat;
`;

// TODO: stop extending Dialog
// Related discussion (different component, same problem):
// https://github.com/idealist7/idealist7/pull/3399/files#diff-647d5809189ddca5d8165fff34bc32d651cbbd4723347ae217d2cfc61fa76f97R18-R21
const StyledDialog = styled(Dialog)<{
  $maxWidth: 'none' | number | undefined;
  $wide: boolean;
  $noPadding: boolean;
}>`
  ${getModalBackgroundImage}
  padding: ${(props) => (props.$noPadding ? undefined : '42px 20px 35px')};
  max-width: ${(props) => {
    if (typeof props.$maxWidth === 'undefined') return;

    if (props.$maxWidth === 'none') return 'none';

    return `${props.$maxWidth}px}`;
  }};

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: ${(props) => (props.$noPadding ? undefined : '40px')};
    ${(props) => props.$wide && `min-width: 650px`}
  }
`;

const StyledCloseButton = styled(CloseButton)`
  color: ${colors.mediumContentGrey};
  height: 24px;
  width: 24px;

  &:hover {
    color: ${colors.brandBlue};

    &:disabled {
      &:hover {
        color: ${colors.lightContentGrey};
      }
    }
  }
`;

type SharedProps = {
  className?: string;
  uncloseable?: boolean;
  isCloseButtonVisible?: boolean;
  image?: ReactNode;
  title?: ReactNode;
  onClose?: (reason?: ModalDismissalReason) => void;
  qaId?: string;
  wide?: boolean;
  maxWidth?: 'none' | number;
  noPadding?: boolean;
  // NOTE: use this to allow modal content host images larger than its main
  // body, for instance animations attached to the container like here:
  // Example: https://app.shortcut.com/idealisttechproduct/story/62752
  // (modals automatically hide vertical (block) overflow, so we don't need to change both)
  inlineOverflow?: CSSProperties['overflow'];
  customOverlay?: ReactNode;
};

type PropsWithChildren = SharedProps & {
  children: ReactNode;
  render?: undefined;
};

type PropsWithRender = SharedProps & {
  render: (renderProps: { qaId?: string }) => ReactNode;
  children?: undefined;
};

export type ModalProps = PropsWithChildren | PropsWithRender;

export function Modal({
  children,
  className,
  uncloseable,
  isCloseButtonVisible = true,
  title,
  image,
  onClose,
  wide,
  qaId,
  maxWidth,
  noPadding,
  inlineOverflow = 'auto',
  customOverlay,
  render,
}: ModalProps) {
  const isMounted = useIsMounted();

  useHideDocumentScroll({
    show: isMounted(),
  });

  const id = useId();

  if (render)
    return (
      <ModalBase
        className={className}
        onClose={onClose}
        aria-labelledby={id}
        inlineOverflow={inlineOverflow}
        customOverlay={customOverlay}
      >
        {render({
          qaId,
        })}
      </ModalBase>
    );

  return (
    <ModalBase
      className={className}
      onClose={onClose}
      aria-labelledby={id}
      inlineOverflow={inlineOverflow}
      customOverlay={customOverlay}
    >
      <StyledDialog
        data-qa-id={qaId}
        $wide={Boolean(wide)}
        $maxWidth={maxWidth}
        $noPadding={Boolean(noPadding)}
      >
        {onClose && !uncloseable && isCloseButtonVisible && (
          <StyledCloseButton
            onClick={(e: React.MouseEvent) =>
              onClose({ type: 'click-x', originalEvent: e })
            }
            qaId="close-modal-button"
            data-modal-focus="false"
          />
        )}

        {image}

        <div>
          {title && <ModalTitle id={id}>{title}</ModalTitle>}
          {children}
        </div>
      </StyledDialog>
    </ModalBase>
  );
}
